import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'react-bootstrap';

import { selectUserIsAdmin } from 'redux/reducers/authReducer';
import { setCurrentTab } from 'redux/reducers/fluxUploadLayoutReducer';
import Checkbox from 'components/DesignSystem/Checkbox';
import StudyRow from 'components/Flux/Upload/StudyRow';
import StudyCard from 'components/Flux/Upload/Mobile/StudyCard';
import MyStudies from 'components/Flux/Personalized/MyStudies';
import { UPLOAD_PRODUCT } from 'components/Flux/Upload/MyStudies/product';
import featureFlags from 'constants/featureFlags';
import EmptyListAlert from 'components/EmptyListAlert';
import { useListStudiesQuery } from 'services/api/studies';
import { useHandleRequestErrors } from 'utils/hooks';
import { ResponseError } from 'services/api/base';
import useInfiniteScrollPaging from 'utils/useInfiniteScrollPaging';
import useIsMobile from 'utils/hooks/useIsMobile';
import { checkDataCompletion, getDataLength } from 'helper/scrollStopper';
import useListEnd from 'utils/hooks/useListEnd';
import './index.scss';

export default function Upload() {
  const dispatch = useDispatch();

  const isAdmin: boolean = useSelector(selectUserIsAdmin);
  const isMobile = useIsMobile();
  // Nova Meus Estudos só no desktop e atrás da flag; o mobile segue com os cards legados.
  const showNewDesktop = featureFlags.fluxUploadMeusEstudosV2 && !isMobile;
  const [ampereCases, setAmpereCases] = useState<boolean>(isAdmin);
  const [maxLength, setMaxLength] = useState<number>(-1);
  const pageSize = 12;
  const { page } = useInfiniteScrollPaging();
  const pageLength = pageSize * page;
  const dataLength = getDataLength(maxLength, pageLength);

  const { data: studyList, error, isFetching } = useListStudiesQuery({
    ampereCases,
    page: 1,
    pageSize: dataLength,
  }, { skip: showNewDesktop });
  useHandleRequestErrors(error as ResponseError | undefined);

  const { listEnded } = useListEnd(studyList, page, pageSize, isFetching);

  useEffect(() => {
    dispatch(setCurrentTab('list'));
  }, [dispatch]);

  useEffect(() => {
    if (checkDataCompletion(dataLength, isFetching, studyList)) {
      setMaxLength(dataLength);
    }
  }, [dataLength, studyList, isFetching]);

  const studies = studyList?.map((study) => ({
    study,
    key: study.id,
  })) ?? [];

  // Depois de todos os hooks, para a ordem deles não mudar ao cruzar o breakpoint.
  if (showNewDesktop) {
    return <MyStudies product={UPLOAD_PRODUCT} />;
  }

  if (isMobile) {
    const resultCount = studyList?.length ?? 0;

    return (
      <div className="upload-list-mobile">
        <div className="upload-list-mobile-toolbar">
          <span className="upload-list-mobile-count">
            {resultCount}
            {resultCount === 1 ? ' estudo' : ' estudos'}
          </span>
          {isAdmin && (
            <Checkbox
              label="Casos Ampere"
              checked={ampereCases}
              onChange={() => { setAmpereCases(!ampereCases); }}
            />
          )}
        </div>
        <div className="upload-list-mobile-cards">
          <EmptyListAlert
            message="Não foi encontrado nenhum estudo"
            array={studies}
            component={StudyCard}
            isFetching={isFetching}
            listEnded={listEnded}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="list-container pb-1">
      {isAdmin && (
        <div className="d-flex justify-content-end">
          <Checkbox
            className="me-5 mb-1"
            label="Casos Ampere"
            checked={ampereCases}
            onChange={() => { setAmpereCases(!ampereCases); }}
          />
        </div>
      )}
      <Row>
        <Col md={4} className="special-header">
          DESCRIÇÃO
        </Col>
        <Col md={4} className="special-header">
          HORIZONTE
        </Col>
        <Col md={4}>
          STATUS
        </Col>
      </Row>
      <EmptyListAlert
        message="Não foi encontrado nenhum estudo"
        array={studies}
        component={StudyRow}
        isFetching={isFetching}
        listEnded={listEnded}
      />
    </div>
  );
}
